import { useCallback, useRef, useState, type CSSProperties } from 'react';
import styled from '@emotion/styled';
import { ClickAwayListener } from '@mui/material';
import { CalendarDots } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import type { Dayjs } from 'dayjs';
import { ClearFieldOption } from '../bulk/ClearFieldOption';
import { getDateFromValue } from 'components/inputs/DatePicker';
import { formatDateTime } from 'utils';
import { Text } from 'components';
import { Tooltip } from 'components/tooltip';
import { TableTooltip } from '../../TableTooltip';
import { useEscapeToClose, useEnterToClose, useClickOutside, useFocusOnEdit } from '../../../hooks';
import {
  useEditableContextOptional,
  useShouldSkipInlineCommit,
} from '../../OptimizedTableProvider';
import { StyledDatePicker } from '../../tableStyles';
import { prepareInlineCellHandoff, readActiveEditableSlot } from './commitActiveEditor';
import type { DatePickerCellProps } from '../../../components/types';
import {
  DATE_CELL_BASE,
  DATE_CELL_HOVER,
  DATE_CELL_EDITING,
  DATE_CELL_ERROR,
  DATE_CELL_ERROR_TEXT,
  DATE_CELL_DISABLED,
  DATE_CELL_DISABLED_TEXT,
  DATE_PLACEHOLDER_STYLE,
} from './dateInlineEditStyles';

/** Default date format */
export const defaultDateFormat = 'dd-LLL-yyyy';

/** Selector for DatePicker popover so we can ignore clicks on the calendar when closing */
const CALENDAR_POPOVER_SELECTOR = '[data-testid="calendar-popover"]';

/**
 * Maps an unparseable typed value to a user-facing error: a date-like value
 * (digits + separators) reads as an impossible date, otherwise a format hint.
 */
const getDateFormatError = (typedVal: string): string => {
  const hasDigitAndSep = /\d/.test(typedVal) && /[-/.\s]/.test(typedVal);
  return hasDigitAndSep ? "That date doesn't exist" : 'Use the format DD/MM/YYYY';
};

/**
 * DatePickerCell - A self-contained inline-editable date cell for tables
 *
 * Features:
 * - Click to edit with DatePicker
 * - Three-dots menu with Edit option
 * - Validation and error display
 * - Optional tooltip
 * - Configurable date format
 * - Visual states: hover, editing, error, disabled (per Figma dl-59431)
 * - No dependencies on legacy EditableCell component (bypasses its problematic useEffect)
 *
 * @example
 * ```tsx
 * <DatePickerCell
 *   context={context}
 *   value={row.deadline}
 *   onUpdate={(date) => updateItem(row.id, { deadline: date })}
 *   editableRowIndex={editableRowIndex}
 *   editableColumnId={editableColumnId}
 *   tooltipContent={<span>Created: {row.createdDate}</span>}
 * />
 * ```
 */
export const DatePickerCell = <T extends { id: number | string }>({
  context,
  value,
  onUpdate,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  dateFormat = defaultDateFormat,
  placeholder = '',
  tooltipContent,
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
  required: requiredProp,
  requiredMessage,
  error: errorProp,
  disabled: disabledProp,
}: DatePickerCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClickAway =
    onClickAwayProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const shouldSkipCommit = useShouldSkipInlineCommit();

  const [isHovered, setIsHovered] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const dateJustSelectedRef = useRef(false);
  const lastTypedRef = useRef('');

  const formattedValue = value ? formatDateTime(value.toString(), dateFormat) : '';

  // Commits the typed value: saves a valid date, shows an inline error for an
  // invalid one, clears when emptied, or just closes when unchanged. Shared by
  // click-away and Enter so typing is persisted by either action.
  const handleClickOutside = useCallback(() => {
    if (dateJustSelectedRef.current) return;
    if (shouldSkipCommit()) return;
    const typedVal = lastTypedRef.current.trim();
    if (!typedVal) {
      if (requiredProp && formattedValue) {
        setInlineError(requiredMessage ?? t`This field is mandatory for Ready for Export items`);
        return;
      }
      if (formattedValue) onUpdate('');
      setIsHovered(false);
      onClickAway?.();
      return;
    }
    if (typedVal !== formattedValue) {
      const parsed = getDateFromValue(typedVal);
      if (parsed?.isValid()) {
        setIsHovered(false);
        onUpdate(parsed.format('DD-MMM-YYYY'));
        onClickAway?.();
        return;
      }
      setInlineError(getDateFormatError(typedVal));
      return;
    }
    setInlineError('');
    onClickAway?.();
  }, [formattedValue, onClickAway, onUpdate, shouldSkipCommit]);

  // Escape abandons the edit: drop any pending inline error so its (controlled)
  // tooltip doesn't stay pinned to the read-mode cell after closing.
  const handleEscape = useCallback(() => {
    setInlineError('');
    onClickAway?.();
  }, [onClickAway]);

  useEscapeToClose(isEditing, handleEscape);
  useEnterToClose(isEditing, handleClickOutside, true);
  useClickOutside(editorContainerRef, handleClickOutside, isEditing, CALENDAR_POPOVER_SELECTOR);

  useFocusOnEdit(editorContainerRef, isEditing, 'input', true);

  // Activate on mousedown rather than click: when another cell is mid-edit, its
  // blur-save (fired during this mousedown's focus change) triggers an optimistic
  // update that rebuilds the columns and remounts every cell between mousedown and
  // mouseup, so the click never fires here (DL-60264). The microtask defer lets the
  // outgoing cell's blur/click-away reset run first so it doesn't clobber us.
  const handleActivate = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (!isEditing && !disabledProp && context.row.original.id !== -1) {
        // Seed with the current value so an untouched open/close (empty typed
        // buffer) doesn't read as "user erased the field" and clear the date.
        lastTypedRef.current = formattedValue;
        // Reset the calendar-selection guard on every re-open: onCalendarClose
        // isn't guaranteed to fire on a programmatic close, so a stale `true`
        // here would make handleClickOutside no-op on later edits of this cell.
        dateJustSelectedRef.current = false;
        setInlineError('');
        prepareInlineCellHandoff(
          () => readActiveEditableSlot(editableCtx),
          () => setEditableState({ rowIndex: null, columnId: null }),
          context.row.index,
          context.column.id
        );
        Promise.resolve().then(() => onCellEdit?.(context));
      }
    },
    [isEditing, disabledProp, context, onCellEdit, formattedValue, editableCtx, setEditableState]
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- event.target is always an Element in practice
      if ((event.target as HTMLElement).closest?.(CALENDAR_POPOVER_SELECTOR)) return;
      handleClickOutside();
    },
    [handleClickOutside]
  );

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const hasError = !!errorProp;
  const showHover = isHovered && !isEditing && !disabledProp;

  const cellStateStyle: CSSProperties = {
    ...DATE_CELL_BASE,
    ...(showHover && !hasError ? DATE_CELL_HOVER : {}),
    ...(showHover && hasError
      ? {
          backgroundColor: DATE_CELL_HOVER.backgroundColor,
          cursor: DATE_CELL_HOVER.cursor,
        }
      : {}),
    ...(disabledProp ? DATE_CELL_DISABLED : {}),
    ...(isEditing ? DATE_CELL_EDITING : {}),
    ...(hasError ? DATE_CELL_ERROR : {}),
  };

  const textStyle: CSSProperties = {
    ...(hasError ? DATE_CELL_ERROR_TEXT : {}),
    ...(disabledProp ? DATE_CELL_DISABLED_TEXT : {}),
  };

  // Read mode content — underscore placeholder for empty editable cells
  const readModeContent = formattedValue ? (
    <Text weight="regular" variant="bodyXs" style={textStyle}>
      {formattedValue}
    </Text>
  ) : (
    <span style={{ ...DATE_PLACEHOLDER_STYLE, ...textStyle }}>_</span>
  );

  // Show calendar icon on hover/focus only (not editing, not disabled, not error)
  const showCalendarIcon = showHover && !hasError;

  return (
    <Tooltip
      title={inlineError || ''}
      open={!!inlineError}
      arrow
      error
      placement="top-start"
      followCursor={false}
    >
      <CellWrapper
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={cellStateStyle}
      >
        <CellContainer
          onMouseDown={handleActivate}
          tooltipTitle={isEditing ? undefined : tooltipContent || formattedValue}
        >
          {isEditing ? (
            <ClickAwayListener onClickAway={handleClickAway}>
              <EditingWrapper ref={editorContainerRef}>
                <EditorContainer>
                  <StyledDatePicker
                    autoOpen
                    hideBackdrop
                    calendarPosition="below"
                    errorIcon={false}
                    formatErrorHelperText={inlineError || undefined}
                    error={!!inlineError || errorProp}
                    helperText={inlineError || undefined}
                    id={`date-picker-${context.column.id}`}
                    placeholder={placeholder}
                    hideClear
                    initialDate={formattedValue}
                    calendarFooter={
                      formattedValue && !requiredProp ? (
                        <ClearFieldOption
                          handleClick={() => {
                            setIsHovered(false);
                            setInlineError('');
                            onUpdate('');
                          }}
                        />
                      ) : undefined
                    }
                    onCalendarClose={() => {
                      setTimeout(() => {
                        dateJustSelectedRef.current = false;
                      }, 0);
                    }}
                    onBlur={() => {
                      const typedVal = lastTypedRef.current.trim();
                      if (typedVal) {
                        const parsed = getDateFromValue(typedVal);
                        if (!parsed?.isValid()) {
                          setInlineError(getDateFormatError(typedVal));
                        }
                      }
                    }}
                    onChange={(
                      _event: React.SyntheticEvent,
                      date: Dayjs | null,
                      reason: string,
                      stringDate: string
                    ) => {
                      if (reason === 'input') {
                        lastTypedRef.current = stringDate;
                        if (inlineError) setInlineError('');
                      } else if (reason === 'selectOption') {
                        lastTypedRef.current = stringDate ?? '';
                        setInlineError('');
                      }
                      if (reason === 'selectOption' && date && stringDate) {
                        dateJustSelectedRef.current = true;
                        setIsHovered(false);
                        onUpdate(stringDate);
                      }
                    }}
                  />
                </EditorContainer>
              </EditingWrapper>
            </ClickAwayListener>
          ) : (
            readModeContent
          )}
        </CellContainer>

        {showCalendarIcon && (
          <CalendarIconWrapper onMouseDown={handleActivate}>
            <CalendarDots size={20} weight="light" />
          </CalendarIconWrapper>
        )}
      </CellWrapper>
    </Tooltip>
  );
};

// ============================================================================
// Styled Components
// ============================================================================

const CellWrapper = styled.div`
  position: relative;
`;

const CalendarIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
  color: ${p => p.theme.palette.fg.primary};
`;

const EditingWrapper = styled.div`
  display: flex;
  align-items: center;
  width: calc(100% + 24px);
  margin-right: -24px;
  overflow: visible;
`;

const EditorContainer = styled.div`
  flex: 0 1 auto;
  min-width: 20px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: visible;

  && .MuiFormControl-root {
    width: 100%;
    flex-direction: row;
    gap: 0;
  }

  && .MuiInputBase-root {
    font-size: 0.75rem;
    width: 100%;
    padding: 0;

    &::before {
      border-bottom: 0.5px solid ${p => p.theme.palette.border.primarySubtle};
    }

    &::after {
      display: none;
    }

    input {
      padding: 0;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .MuiInputAdornment-root {
    display: none;
  }

  .MuiFormHelperText-root {
    display: none;
  }
`;

const CellContentContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1;
  min-width: 0;
  overflow: visible;

  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CellContainer = ({
  children,
  tooltipTitle,
  onMouseDown,
}: {
  children: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  onMouseDown?: (e: React.MouseEvent) => void;
}) => {
  return tooltipTitle ? (
    <TableTooltip title={tooltipTitle}>
      <CellContentContainer onMouseDown={onMouseDown}>{children}</CellContentContainer>
    </TableTooltip>
  ) : (
    <CellContentContainer onMouseDown={onMouseDown}>{children}</CellContentContainer>
  );
};
